import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import {
  connectBattleSocket,
  emitBattleEvent,
  emitBattleEventWhenReady,
  onBattleEvent,
  subscribeBattlePresence,
} from "@/socket/battleSocket"
import { getRadarStatus } from "@/api/gameApi"

export default function MatchmakingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const mode = searchParams.get("mode") === "QR" ? "QR" : "RADAR"
  const role =
    searchParams.get("role") === "guest"
      ? "guest"
      : searchParams.get("role") === "host"
        ? "host"
        : mode === "QR"
          ? "host"
          : undefined

  const [online, setOnline] = useState<number | null>(null)
  const [status, setStatus] = useState<string>("")
  const [busy, setBusy] = useState(false)
  const [roomCode, setRoomCode] = useState("")
  const [hostedCode, setHostedCode] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void connectBattleSocket().catch(() => {
      if (!cancelled) setStatus("Не удалось подключиться к battle socket")
    })
    const unsubPresence = subscribeBattlePresence((s) => {
      if (!cancelled) setOnline(s.onlineCount)
    })
    const offStarted = onBattleEvent("battle-started", (payload) => {
      navigate("/play/duel", { state: { battle: payload } })
    })
    const offQrCreated = onBattleEvent("qr-room-created", (payload) => {
      setHostedCode(payload.roomCode)
      setStatus(`Комната: ${payload.roomCode}`)
    })
    const offQrExpired = onBattleEvent("qr-room-expired", (p) => {
      setHostedCode(null)
      setStatus(p.message || "QR-комната истекла")
    })
    return () => {
      cancelled = true
      unsubPresence()
      offStarted()
      offQrCreated()
      offQrExpired()
    }
  }, [navigate])

  useEffect(() => {
    if (mode === "QR" && role === "host") {
      void startQrHost()
    }
    return () => {
      if (mode === "QR" && role === "host") {
        emitBattleEvent("qr-cancel")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-host once on mount
  }, [mode, role])

  async function refreshRadar() {
    try {
      const s = await getRadarStatus()
      setStatus(
        `В очереди: ${s.queueSize ?? 0}, доступно для матча: ${s.matchableCount ?? 0}`
      )
    } catch {
      setStatus("Не удалось получить статус радара")
    }
  }

  function startRadar() {
    setBusy(true)
    emitBattleEvent("radar-search", {})
    void refreshRadar().finally(() => setBusy(false))
  }

  function cancelRadar() {
    emitBattleEvent("radar-cancel")
    void refreshRadar()
  }

  async function startQrHost() {
    setBusy(true)
    const sent = await emitBattleEventWhenReady(null, "qr-create")
    if (!sent) setStatus("Сокет не готов — повторите")
    setBusy(false)
  }

  async function joinQr() {
    const code = roomCode.trim().toUpperCase()
    if (!code) {
      setStatus("Введите код комнаты")
      return
    }
    setBusy(true)
    const sent = await emitBattleEventWhenReady(null, "qr-join", { roomCode: code })
    if (!sent) setStatus("Сокет не готов")
    setBusy(false)
  }

  function cancelQrHost() {
    emitBattleEvent("qr-cancel")
    setHostedCode(null)
    setStatus("Комната закрыта")
  }

  const title = mode === "QR" ? "QR-бой" : "Радар"

  return (
    <GameScreenLayout title={title} backTo="/play">
      <p className="text-sm text-guardians-muted">
        Онлайн (battle): {online ?? "…"}
      </p>
      <p className="mt-2 text-sm text-guardians-muted">{status || "—"}</p>

      {mode === "RADAR" ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={startRadar}
            className="rounded-xl bg-guardians-accent px-4 py-2 text-sm font-medium text-guardians-bg disabled:opacity-60"
          >
            Искать бой
          </button>
          <button
            type="button"
            onClick={cancelRadar}
            className="rounded-xl border border-white/20 px-4 py-2 text-sm"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => void refreshRadar()}
            className="rounded-xl border border-white/20 px-4 py-2 text-sm"
          >
            Обновить
          </button>
          <button
            type="button"
            onClick={() => navigate("/play/matchmaking?mode=QR&role=host")}
            className="rounded-xl border border-white/20 px-4 py-2 text-sm"
          >
            QR-хост
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {role === "host" ? (
            <>
              {hostedCode ? (
                <p className="text-lg font-mono tracking-widest text-guardians-accent">
                  {hostedCode}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void startQrHost()}
                  className="rounded-xl bg-guardians-accent px-4 py-2 text-sm text-guardians-bg"
                >
                  Создать комнату
                </button>
                <button
                  type="button"
                  onClick={cancelQrHost}
                  className="rounded-xl border border-white/20 px-4 py-2 text-sm"
                >
                  Закрыть
                </button>
              </div>
            </>
          ) : (
            <>
              <input
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                placeholder="Код комнаты"
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 font-mono text-sm tracking-widest"
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => void joinQr()}
                className="rounded-xl bg-guardians-accent px-4 py-2 text-sm text-guardians-bg"
              >
                Войти
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => navigate("/play/matchmaking")}
            className="text-xs text-guardians-accent underline"
          >
            ← Радар
          </button>
          <button
            type="button"
            onClick={() => navigate("/play/matchmaking?mode=QR&role=guest")}
            className="ml-3 text-xs text-guardians-accent underline"
          >
            Гость QR
          </button>
        </div>
      )}
    </GameScreenLayout>
  )
}
